import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { Badge } from '@/components/ui/badge';
import { Slider } from '@/components/ui/slider';
import { SensorConfig, SensorType } from '@/lib/sensor-types';
import { sensorManager } from '@/lib/sensor-integration';

interface SensorCalibrationPanelProps {
  sensorId: string;
  onCalibrationComplete: (config: SensorConfig) => void;
}

export const SensorCalibrationPanel: React.FC<SensorCalibrationPanelProps> = ({
  sensorId,
  onCalibrationComplete
}) => {
  const [calibrationStep, setCalibrationStep] = useState(0);
  const [progress, setProgress] = useState(0);
  const [baseline, setBaseline] = useState(0);
  const [sensitivity, setSensitivity] = useState([50]);
  const [isCalibrating, setIsCalibrating] = useState(false);

  const calibrationSteps = [
    'Sensor Detection',
    'Baseline Measurement',
    'Sensitivity Adjustment',
    'Validation Test',
    'Complete'
  ];

  const startCalibration = async () => {
    setIsCalibrating(true);
    setCalibrationStep(0);
    setProgress(0);

    // Simulate calibration process
    for (let step = 0; step < calibrationSteps.length; step++) {
      setCalibrationStep(step);
      setProgress((step / (calibrationSteps.length - 1)) * 100);
      
      await new Promise(resolve => setTimeout(resolve, 2000));
      
      if (step === 1) {
        // Simulate baseline measurement
        setBaseline(Math.random() * 100 + 50);
      }
    }

    const config: SensorConfig = {
      id: sensorId,
      type: SensorType.HEART_RATE, // Default, would be detected
      name: `Sensor ${sensorId}`,
      sampleRate: 100,
      calibrated: true,
      connected: true,
      baseline,
      sensitivity: sensitivity[0] / 100
    };

    onCalibrationComplete(config);
    setIsCalibrating(false);
  };

  return (
    <Card className="w-full max-w-md mx-auto">
      <CardHeader>
        <CardTitle className="flex items-center justify-between">
          Sensor Calibration
          <Badge variant={isCalibrating ? "default" : "secondary"}>
            {isCalibrating ? "Calibrating" : "Ready"}
          </Badge>
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-6">
        <div className="space-y-2">
          <div className="flex justify-between text-sm">
            <span>Progress</span>
            <span>{Math.round(progress)}%</span>
          </div>
          <Progress value={progress} className="w-full" />
        </div>

        <div className="space-y-3">
          {calibrationSteps.map((step, index) => (
            <div
              key={step}
              className={`flex items-center space-x-3 p-2 rounded ${
                index === calibrationStep ? 'bg-blue-50 border border-blue-200' :
                index < calibrationStep ? 'bg-green-50 border border-green-200' :
                'bg-gray-50 border border-gray-200'
              }`}
            >
              <div className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold ${
                index < calibrationStep ? 'bg-green-500 text-white' :
                index === calibrationStep ? 'bg-blue-500 text-white' :
                'bg-gray-300 text-gray-600'
              }`}>
                {index < calibrationStep ? '✓' : index + 1}
              </div>
              <span className="text-sm">{step}</span>
            </div>
          ))}
        </div>

        {calibrationStep === 2 && (
          <div className="space-y-3">
            <label className="text-sm font-medium">Sensitivity: {sensitivity[0]}%</label>
            <Slider
              value={sensitivity}
              onValueChange={setSensitivity}
              max={100}
              min={10}
              step={5}
              className="w-full"
            />
          </div>
        )}

        {baseline > 0 && (
          <div className="p-3 bg-blue-50 rounded-lg">
            <div className="text-sm font-medium text-blue-800">
              Baseline Reading: {baseline.toFixed(2)}
            </div>
          </div>
        )}

        <Button
          onClick={startCalibration}
          disabled={isCalibrating}
          className="w-full"
        >
          {isCalibrating ? 'Calibrating...' : 'Start Calibration'}
        </Button>
      </CardContent>
    </Card>
  );
};